import { useEffect, type RefObject } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { VendorWriteType } from '@econtractor/types';
import spinnerTitle from 'components/others/spinnerTitle';
import SvgSave from 'components/svg/SvgSave';
import { ClientPhoneField, ContactStartWithField } from 'modules/client';

interface VendorEditFormProps {
    values: VendorWriteType;
    loading?: boolean;
    submitLabel: string;
    section: 'general' | 'addresses';
    nameInputRef?: RefObject<HTMLInputElement | null>;
    onChange: (field: keyof VendorWriteType, value: string) => void;
    onSubmit: () => Promise<void>;
}

const VendorEditForm = ({
    values,
    loading = false,
    submitLabel,
    section,
    nameInputRef,
    onChange,
    onSubmit,
}: VendorEditFormProps) => {
    const { t } = useTranslation(['Vendors', 'common']);

    useEffect(() => {
        if (nameInputRef?.current) {
            nameInputRef.current.focus();
        }
    }, [nameInputRef]);

    return (
        <Form
            className="d-flex flex-column h-100"
            onSubmit={async (e) => {
                e.preventDefault();
                await onSubmit();
            }}
        >
            <div className="off-vendor-edit-form d-flex flex-column h-100">
                <div className="off-vendor-edit-content px-3">
                    {section === 'general' ? (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('edit.fields.vendorName')}</Form.Label>
                                <Form.Control
                                     
                                    required
                                    ref={nameInputRef}
                                    value={values.vendorName || ''}
                                    onChange={(e) => onChange('vendorName', e.target.value)}
                                />
                            </Form.Group>

                            <ClientPhoneField
                                label={t('edit.fields.vendorPhone')}
                                value={values.vendorPhone || ''}
                                onChange={(value) => onChange('vendorPhone', value)}
                            />

                            <Form.Group className="mb-3">
                                <Form.Label>{t('edit.fields.vendorEmail')}</Form.Label>
                                <Form.Control
                                    type="email"
                                    autoComplete="email"
                                    value={values.vendorEmail || ''}
                                    onChange={(e) => onChange('vendorEmail', e.target.value)}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>{t('edit.fields.vendorLegalName')}</Form.Label>
                                <Form.Control
                                    value={values.vendorLegalName || ''}
                                    onChange={(e) => onChange('vendorLegalName', e.target.value)}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>{t('edit.fields.vendorWebsite')}</Form.Label>
                                <ContactStartWithField
                                    startWith="https://"
                                    value={values.vendorWebsite || ''}
                                    onChange={(value) => onChange('vendorWebsite', value)}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>{t('edit.fields.vendorNote')}</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={values.vendorNote || ''}
                                    onChange={(e) => onChange('vendorNote', e.target.value)}
                                />
                            </Form.Group>
                        </>
                    ) : (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('edit.fields.vendorLegalAddress')}</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={values.vendorLegalAddress || ''}
                                    onChange={(e) => onChange('vendorLegalAddress', e.target.value)}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>{t('edit.fields.vendorActualAddress')}</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={values.vendorActualAddress || ''}
                                    onChange={(e) => onChange('vendorActualAddress', e.target.value)}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>{t('edit.fields.vendorWarehouseAddress')}</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={values.vendorWarehouseAddress || ''}
                                    onChange={(e) => onChange('vendorWarehouseAddress', e.target.value)}
                                />
                            </Form.Group>
                        </>
                    )}
                </div>

                <div className="p-3 offcanvas-footer off-vendor-edit-footer">
                    <div className="d-flex justify-content-end">
                        <Button type="submit" variant="info" disabled={loading} className="min100px">
                            {spinnerTitle(
                                <>
                                    <SvgSave className="me-2 mtop-2" />
                                    {submitLabel}
                                </>,
                                loading,
                                t('common:buttons.saving'),
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </Form>
    );
};

export default VendorEditForm;
